import React, { createContext, useReducer, useCallback } from "react";
import {
  NotificationContextType,
  NotificationState,
  NotificationType,
  ProviderProps,
} from "../types";
import { translateApiError } from "../../utils/translateApiError";

const initialState: NotificationState = {
  isOpen: false,
  message: undefined,
  type: undefined,
};

export const NotificationContext = createContext<NotificationContextType>({
  ...initialState,
  openNotification: () => {},
  openError: () => {},
  closeNotification: () => {},
});

const notificationReducer = (
  state: NotificationState,
  action: any
): NotificationState => {
  switch (action.type) {
    case "OPEN_NOTIFICATION":
      return {
        ...state,
        isOpen: true,
        message: action.message,
        type: action.notificationType,
      };
    case "CLOSE_NOTIFICATION":
      return {
        ...state,
        isOpen: false,
        message: undefined,
        type: undefined,
      };
    default:
      return state;
  }
};

export const NotificationProvider: React.FC<ProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(notificationReducer, initialState);

  const openNotification = useCallback(
    (message: string, type?: NotificationType) => {
      dispatch({
        type: "OPEN_NOTIFICATION",
        message,
        notificationType: type,
      });
    },
    []
  );

  const openError = useCallback(
    (error: unknown) => openNotification(translateApiError(error), "error"),
    [openNotification]
  );

  const closeNotification = useCallback(() => {
    dispatch({ type: "CLOSE_NOTIFICATION" });
  }, []);

  return (
    <NotificationContext.Provider
      value={{
        ...state,
        openNotification,
        openError,
        closeNotification,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};
